import type { InternalAxiosRequestConfig } from "axios";
import { sanitizePathname } from "@/lib/analytics/error-utils";

export interface ApiRequestMeta {
  method: string;
  path: string;
  host?: string;
}

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const resolveRequestUrl = (url?: string, baseURL?: string): string | undefined => {
  if (!url) return undefined;
  if (isAbsoluteUrl(url)) return url;
  if (baseURL && isAbsoluteUrl(baseURL)) {
    try {
      return new URL(url, baseURL).toString();
    } catch {
      return url;
    }
  }
  return url;
};

const extractPath = (value: string): string => {
  const withoutQuery = value.split("?")[0] || "";
  if (!withoutQuery.startsWith("/")) return `/${withoutQuery}`;
  return withoutQuery;
};

export const getHttpRequestMeta = (
  url?: string,
  methodValue?: string,
  baseURL?: string,
): ApiRequestMeta => {
  const method = (methodValue || "GET").toUpperCase();
  const resolvedUrl = resolveRequestUrl(url, baseURL);

  if (resolvedUrl && isAbsoluteUrl(resolvedUrl)) {
    try {
      const parsed = new URL(resolvedUrl);
      return {
        method,
        path: sanitizePathname(parsed.pathname || "/"),
        host: parsed.host,
      };
    } catch {
      return {
        method,
        path: sanitizePathname(extractPath(resolvedUrl)),
      };
    }
  }

  const path = resolvedUrl || url || "/unknown";
  return {
    method,
    path: sanitizePathname(extractPath(path)),
  };
};

export const getApiRequestMeta = (config: InternalAxiosRequestConfig): ApiRequestMeta => {
  return getHttpRequestMeta(config.url, config.method, config.baseURL);
};

export const getCurrentRoutePath = () => {
  if (typeof window === "undefined") return "/";
  return sanitizePathname(window.location.pathname);
};
